// src/components/tasks/TaskAttachments.tsx
//
// Task attachments + proof, built entirely on the existing Files infrastructure:
// uploads go through uploadUtils (presign → PUT → register) with { taskId, isProof }
// extra metadata, and the list comes from the existing /files API filtered by
// taskId. No separate attachment system.

import { useRef, useState } from 'react';
import { Paperclip, ShieldCheck, X, FileText, Loader2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useGetFilesQuery, useDeleteFileMutation } from '../../api/filesApi';
import { uploadFiles } from '../../utils/uploadUtils';
import type { FileAsset } from '../../types/api';

function formatSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface TaskAttachmentsProps {
  taskId: string;
  workspaceId: string;
  /** When true, the Proof group is emphasized (completion requires proof). */
  proofRequired?: boolean;
  /** Notified with the current proof-attachment count after loads/changes. */
  onProofCountChange?: (count: number) => void;
}

export default function TaskAttachments({
  taskId,
  workspaceId,
  proofRequired,
  onProofCountChange,
}: TaskAttachmentsProps) {
  const { token } = useAuth();
  const { data: files, refetch } = useGetFilesQuery({ workspaceId, taskId }, { skip: !workspaceId || !taskId });
  const [deleteFile] = useDeleteFileMutation();

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);
  const proofInputRef = useRef<HTMLInputElement>(null);

  const attachments = (files ?? []).filter((f) => !f.isProof);
  const proofs = (files ?? []).filter((f) => f.isProof);

  // Keep the parent (modal) in sync with the live proof count for client gating.
  const lastReported = useRef<number | null>(null);
  if (files && onProofCountChange && lastReported.current !== proofs.length) {
    lastReported.current = proofs.length;
    onProofCountChange(proofs.length);
  }

  const handleSelect = async (e: React.ChangeEvent<HTMLInputElement>, isProof: boolean) => {
    const picked = e.target.files;
    if (!picked || picked.length === 0) return;
    const list = Array.from(picked);
    e.target.value = '';
    if (!token) {
      setError('Not authenticated — cannot upload.');
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const results = await uploadFiles(list, token, { taskId, isProof });
      if (results.length < list.length) {
        setError(`${list.length - results.length} file(s) failed to upload.`);
      }
      await refetch();
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteFile(id).unwrap();
      await refetch();
    } catch {
      setError('Failed to remove file.');
    }
  };

  const renderRow = (f: FileAsset) => {
    const isImage = !!f.mimeType?.startsWith('image/') && !!f.publicUrl;
    return (
      <li key={f.id} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded">
        {isImage ? (
          <img src={f.publicUrl!} alt={f.originalName} className="h-8 w-8 rounded object-cover flex-shrink-0" />
        ) : (
          <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
        )}
        <a
          href={f.publicUrl ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 min-w-0 text-sm text-gray-800 truncate hover:underline"
        >
          {f.originalName}
        </a>
        <span className="text-xs text-gray-400 flex-shrink-0">{formatSize(f.sizeBytes)}</span>
        <button
          type="button"
          onClick={() => handleDelete(f.id)}
          className="text-gray-400 hover:text-red-600 flex-shrink-0"
          aria-label={`Remove ${f.originalName}`}
        >
          <X className="h-4 w-4" />
        </button>
      </li>
    );
  };

  return (
    <div className="space-y-4">
      {/* Attachments group */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-medium text-gray-700">Attachments</span>
          <button
            type="button"
            onClick={() => attachInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline disabled:opacity-50"
          >
            <Paperclip className="h-3.5 w-3.5" /> Add attachment
          </button>
          <input ref={attachInputRef} type="file" multiple className="hidden" onChange={(e) => handleSelect(e, false)} />
        </div>
        {attachments.length > 0 ? (
          <ul className="space-y-1.5">{attachments.map(renderRow)}</ul>
        ) : (
          <p className="text-xs text-gray-400 italic">No attachments.</p>
        )}
      </div>

      {/* Proof group */}
      <div className={proofRequired ? 'rounded-lg border border-amber-200 bg-amber-50 p-3' : ''}>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-medium text-gray-700 inline-flex items-center gap-1">
            <ShieldCheck className={`h-4 w-4 ${proofRequired ? 'text-amber-600' : 'text-gray-400'}`} />
            Proof
            {proofRequired && (
              <span className="ml-1 text-xs font-normal text-amber-700">
                required to complete · {proofs.length} attached
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={() => proofInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline disabled:opacity-50"
          >
            <ShieldCheck className="h-3.5 w-3.5" /> Add proof
          </button>
          <input ref={proofInputRef} type="file" multiple className="hidden" onChange={(e) => handleSelect(e, true)} />
        </div>
        {proofs.length > 0 ? (
          <ul className="space-y-1.5">{proofs.map(renderRow)}</ul>
        ) : (
          <p className={`text-xs italic ${proofRequired ? 'text-amber-700' : 'text-gray-400'}`}>
            {proofRequired ? 'No proof yet — add at least one to complete this task.' : 'No proof attachments.'}
          </p>
        )}
      </div>

      {uploading && (
        <p className="text-xs text-gray-500 inline-flex items-center gap-1">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…
        </p>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
